// One-tap pairing from the launch URL (PRD 007 launch UX; PRD 009 hosted). When
// the room is opened from `/brainstorm`, GetSessionUrl hands back a URL like
//   https://claudepad.io/#/session?session=<id>&token=<pairing>&title=<name>
// (the local gateway's was loopback + token only). This module runs once before
// React mounts: it persists the token (so the web app is paired with no Settings
// detour), stashes the title + session id, then strips them from the URL so the
// token doesn't linger in the address bar or history. The bridge ORIGIN is not in
// the URL — it's the build-time VITE_BRIDGE_API_ORIGIN (config.ts); for the
// legacy local gateway that's empty = same-origin (the gateway served the SPA).

import { saveKeys } from '@/lib/keys/keyStore'
import { setDebugPanelEnabled } from '@/lib/debug/debugParams'

const PENDING_TITLE_KEY = 'convariance.pendingTitle'
const PAIRED_SESSION_KEY = 'convariance.pairedSession'

/** Apply token/title from the current URL hash, if present. Safe to call once. */
export async function autopairFromUrl(): Promise<void> {
  const hash = window.location.hash
  const q = hash.indexOf('?')
  if (q < 0) return
  const params = new URLSearchParams(hash.slice(q + 1))
  const token = params.get('token')
  const title = params.get('title')
  const session = params.get('session')
  // `?debug=1` opens the Debug Panel on load (handy on a /brainstorm room link).
  // It must be read HERE because autopair strips the whole query before React
  // mounts, so a Session-level read would never see it; we persist the panel flag.
  const debug = params.get('debug')
  if (!token && !title && !session && !debug) return

  if (debug === '1' || debug === 'true') setDebugPanelEnabled(true)

  if (title) {
    try {
      sessionStorage.setItem(PENDING_TITLE_KEY, title)
    } catch {
      // sessionStorage unavailable (private mode quota) — title is non-critical
    }
  }
  // The session id is informational for the single-active-session MVP (the Worker
  // routes by the pairing token, not this id), but persist it for the Debug Panel
  // / forward-compat with a real session registry.
  if (session) {
    try {
      sessionStorage.setItem(PAIRED_SESSION_KEY, session)
    } catch {
      // non-critical
    }
  }
  if (token) await saveKeys({ bridgeToken: token })

  // Drop the query from the hash, keep the route (default to the session view).
  const route = hash.slice(0, q) || '#/session'
  history.replaceState(null, '', window.location.pathname + window.location.search + route)
}

/** Read and clear the title carried in from the launch URL, if any. */
export function consumePendingTitle(): string | null {
  try {
    const t = sessionStorage.getItem(PENDING_TITLE_KEY)
    if (t) sessionStorage.removeItem(PENDING_TITLE_KEY)
    return t
  } catch {
    return null
  }
}

/** Read and clear the server session id carried in from the launch URL (PRD 011).
 *  A newly-started session adopts this as its store id so the SPA's snapshot and
 *  the gateway's durable event log + sessions index all key on the SAME id. Null
 *  when the room wasn't opened from a /brainstorm link (a manual New session). */
export function consumePairedSession(): string | null {
  try {
    const s = sessionStorage.getItem(PAIRED_SESSION_KEY)
    if (s) sessionStorage.removeItem(PAIRED_SESSION_KEY)
    return s
  } catch {
    return null
  }
}
